// The app frame: a header, the screen, and a bottom tab bar.
//
// Bottom navigation rather than a drawer, because the brief's core promise is that
// routine logging never requires digging through menus — every tab is one thumb-reach
// from anywhere.

import type { ReactNode } from 'react';

export interface Tab {
  id: string;
  label: string;
  path: string;
  screens: string[];
  icon: ReactNode;
}

const stroke = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.8, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };

export const TABS: Tab[] = [
  {
    id: 'today', label: 'Today', path: '/', screens: ['today', 'checkin'],
    icon: <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true"><path {...stroke} d="M4 6.5h16M4 12h16M4 17.5h10" /></svg>,
  },
  {
    id: 'experiments', label: 'Experiments', path: '/experiments', screens: ['experiments', 'experiment', 'wizard', 'report', 'doctor'],
    icon: <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true"><path {...stroke} d="M4 19l5-7V5m6 14l-5-7V5M8 5h8" /></svg>,
  },
  {
    id: 'record', label: 'Record', path: '/timeline', screens: ['timeline', 'medications', 'scripts', 'profile', 'search'],
    icon: <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true"><path {...stroke} d="M6 4h9l4 4v12H6z M15 4v4h4" /></svg>,
  },
  {
    id: 'settings', label: 'Settings', path: '/settings', screens: ['settings'],
    icon: <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true"><circle {...stroke} cx="12" cy="12" r="3" /><path {...stroke} d="M12 3v2m0 14v2M3 12h2m14 0h2M5.6 5.6l1.4 1.4m10 10l1.4 1.4m0-12.8l-1.4 1.4m-10 10l-1.4 1.4" /></svg>,
  },
];

export function AppShell({ screen, navigate, children, title, back }: {
  screen: string;
  navigate: (path: string) => void;
  children: ReactNode;
  title?: string;
  back?: string;
}) {
  return (
    <div className="min-h-dvh flex flex-col">
      {title && (
        <header className="sticky top-0 z-10 bg-plane/90 backdrop-blur border-b border-hairline
          px-4 py-3 flex items-center gap-3" style={{ paddingTop: 'max(0.75rem, env(safe-area-inset-top))' }}>
          {back && (
            <button
              type="button"
              onClick={() => navigate(back)}
              aria-label="Back"
              className="-ml-2 w-11 h-11 flex items-center justify-center text-ink-2 shrink-0"
            >
              <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
                <path {...stroke} d="M15 5l-7 7 7 7" />
              </svg>
            </button>
          )}
          <h1 className="text-lg font-semibold truncate">{title}</h1>
        </header>
      )}

      <main className="flex-1 px-4 pb-28 pt-4 max-w-2xl w-full mx-auto">{children}</main>

      <nav
        aria-label="Main"
        className="fixed bottom-0 inset-x-0 bg-surface/95 backdrop-blur border-t border-hairline"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        <ul className="flex max-w-2xl mx-auto">
          {TABS.map((tab) => {
            const active = tab.screens.includes(screen);
            return (
              <li key={tab.id} className="flex-1">
                <button
                  type="button"
                  onClick={() => navigate(tab.path)}
                  aria-current={active ? 'page' : undefined}
                  className={`w-full min-h-14 flex flex-col items-center justify-center gap-0.5 py-1.5
                    ${active ? 'text-accent' : 'text-muted'}`}
                >
                  {tab.icon}
                  <span className="text-[11px] font-medium">{tab.label}</span>
                </button>
              </li>
            );
          })}
        </ul>
      </nav>
    </div>
  );
}
